import React, {
  forwardRef,
  PropsWithChildren,
  ReactNode,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import css from './index.module.scss';
import { debounce, noop } from 'lodash';
import { classNames } from '@fullstory/util/fmt';
import { Popover } from '@fullstory/components';
import { VirtualElement } from '@popperjs/core/lib/types';
import { NoElementDOMRect } from '../../Utils';
import { OverlayHighlightDetails } from '@fullstory/page-insights-ui';
import { TooltipPopover } from '@fullstory/components/components/Tooltip/TooltipPopover';
import overlayColors from './overlayColors.module.scss';

interface OverlayHighlightProps {
  className?: string;
  clickOverlayDetail: OverlayHighlightDetails;
  selected?: boolean;
  onSelect: (rank: number) => void;
  hovering: boolean;
  onHover: (mouseEvent: React.MouseEvent<HTMLElement>, isHovering: boolean) => void;
  playbackScaleFactor: number;
  /** The content to display on hover. If not provided, nothing will be shown. */
  tooltipContent?: ReactNode;
  /** Should the Popover be displayed? */
  tooltipOpen?: boolean;
  tooltipVariant?: React.ComponentProps<typeof Popover>['variant'];
  colorClass: keyof typeof overlayColors;
  zIndex?: number;
}

const getBorderWidthBase = ({ selected, hovering }: { selected: boolean; hovering: boolean }) => {
  if (selected) {
    return 3;
  }
  if (hovering) {
    return 2;
  }
  return 1;
};

export type OverlayHighlightHandle = {
  getBoundingClientRect: () => DOMRect;
  getRank: () => number;
};

export type PopoverPlacementType = React.ComponentProps<typeof Popover>['placement'];

/**
 * Determine what position an overlay tooltip should be based on the size of the tooltip,
 * where the overlay is and also how big the playback container is.
 */
const findTooltipPlacement = ({
  overlayPosition,
  tooltipHeight,
  tooltipWidth,
  tooltipPointerSize,
}: {
  overlayPosition?: DOMRect;
  tooltipHeight: number;
  tooltipWidth: number;
  tooltipPointerSize: number;
}): PopoverPlacementType => {
  if (!overlayPosition) {
    return 'top';
  }

  // get the window viewport
  const { innerWidth, innerHeight } = window;

  // calculate the element positions relative to window boundaries
  const willOverlapAbove = overlayPosition.top - (tooltipHeight + tooltipPointerSize) < 0;
  const willOverlapRight =
    overlayPosition.left + overlayPosition.width + (tooltipWidth + tooltipPointerSize) > innerWidth;
  const willOverlapBelow =
    overlayPosition.top + overlayPosition.height + (tooltipHeight + tooltipPointerSize) >
    innerHeight;
  const willOverlapLeft = overlayPosition.left - (tooltipWidth + tooltipPointerSize) < 0;

  let popupPosition: PopoverPlacementType = 'top';

  // determine the final popup position: pick the first usable position
  if (!willOverlapAbove) {
    popupPosition = 'top';
  } else if (!willOverlapRight) {
    popupPosition = 'right';
  } else if (!willOverlapBelow) {
    popupPosition = 'bottom';
  } else if (!willOverlapLeft) {
    popupPosition = 'left';
  }

  // return the (maybe default) popup position
  return popupPosition;
};

// Estimates of the max size the tooltip can be with some small padding
const tooltipSizes = {
  // Height of tooltip. Estimate of max height. Actual is slight smaller than this.
  height: 100,
  // Max height to use
  width: 250,
  // Width/Height of tooltip pointer. Since we can't export SCSS variables, we need to hardcode this
  pointerSize: 10,
};

/**
 * A component used to display a colored overlay over an element.
 */
export const OverlayHighlight = forwardRef<
  OverlayHighlightHandle,
  PropsWithChildren<OverlayHighlightProps>
>(
  (
    {
      className,
      selected = false,
      onSelect,
      hovering,
      onHover,
      playbackScaleFactor,
      clickOverlayDetail,
      colorClass,
      tooltipContent,
      tooltipOpen = false,
      tooltipVariant,
      children,
      zIndex,
    },
    forwardRef,
    ...restOfProps
  ) => {
    const overlayRef = useRef<HTMLDivElement>(null);
    const [isScrolling, setScrolling] = useState<boolean>(false);
    const [virtualElement, setVirtualElement] = useState<VirtualElement>({
      getBoundingClientRect: () => overlayRef?.current?.getBoundingClientRect() ?? NoElementDOMRect,
    });

    const { clientRect, rank } = clickOverlayDetail;

    useImperativeHandle(
      forwardRef,
      () => ({
        getBoundingClientRect: () =>
          overlayRef?.current?.getBoundingClientRect() ?? NoElementDOMRect,
        getRank: () => rank,
      }),
      [rank],
    );

    // We need pointer events for hover actions but when the user is trying to scroll we want those events
    // to flow to the playback container so the page itself can be scrolled
    const resetScrolling = debounce(() => setScrolling(false), 250);
    const onScroll = () => {
      setScrolling(true);
      resetScrolling();
    };

    const { height, width, left, top } = clientRect;

    // Need to use a virtual element here since anchor doesn't listen for scrolls
    useEffect(() => {
      setVirtualElement({
        getBoundingClientRect: () =>
          overlayRef?.current?.getBoundingClientRect() ?? NoElementDOMRect,
      });
    }, [left, top]);

    return (
      <>
        <div
          data-component="OverlayHighlight"
          className={classNames(className, css.overlayHighlight, overlayColors[colorClass], {
            [css.scrolling]: isScrolling,
            [css.selected]: selected,
            [css.hover]: hovering,
          })}
          style={{
            height,
            width,
            position: 'absolute',
            top,
            left,
            borderWidth: getBorderWidthBase({ hovering, selected }) * playbackScaleFactor,
            zIndex,
          }}
          role="none"
          onWheel={onScroll}
          onMouseMove={e => onHover(e, true)}
          onMouseOut={e => onHover(e, false)}
          onClick={() => {
            onSelect(rank);
          }}
          onBlur={noop}
          ref={overlayRef}
          {...restOfProps}
        >
          {children}
        </div>
        {tooltipContent && tooltipOpen && (
          <TooltipPopover
            placement={findTooltipPlacement({
              overlayPosition: overlayRef?.current?.getBoundingClientRect(),
              tooltipHeight: tooltipSizes.height,
              tooltipWidth: tooltipSizes.width,
              tooltipPointerSize: tooltipSizes.pointerSize,
            })}
            anchor={null}
            virtualAnchor={virtualElement}
            open={tooltipOpen}
            tooltipContent={tooltipContent}
            hideTooltip={noop}
            persistTooltip={noop}
            maxWidth={tooltipSizes.width}
          >
            {tooltipContent}
          </TooltipPopover>
        )}
      </>
    );
  },
);

OverlayHighlight.displayName = 'OverlayHighlight';
